/** Conteúdo textual da landing da rota raiz: SEO, FAQ, CTAs (PT-BR, frases curtas). */

/** Classe global em `legalPrimeSiteLanding.global.css`: mantém texto branco nos CTAs em gradiente (evita `a:hover` rosa do escopo da landing). */
export const LEGAL_PRIME_LANDING_CTA_PRIMARY_CLASS = 'legalPrimeSiteLandingCtaPrimary'

export const LANDING_WHATSAPP_PHONE_E164 = '5548984281160'

export function buildLandingWhatsappLink(message: string): string {
  const encoded = encodeURIComponent(message)
  return `https://api.whatsapp.com/send/?phone=${LANDING_WHATSAPP_PHONE_E164}&text=${encoded}&type=phone_number&app_absent=0`
}

export function buildCampaignContactWhatsappLink(campaignTitle: string): string {
  const sorteioLabel = campaignTitle.trim() || 'esta campanha'
  return buildLandingWhatsappLink(`Olá! Tenho dúvidas sobre o sorteio ${sorteioLabel}.`)
}

export const indexLandingWhatsappMessages = {
  diagnosticoHero:
    'Quero criar minha campanha e LUCRAR. Me chama! Site: https://sorteios.com.br/',
  diagnosticoSocial:
    'Vim do site. Quero montar campanha com prêmios e lucrar no caminho certo. sorteios.com.br',
} as const

export const indexLandingSeo = {
  browserTitle: 'Crie sua campanha, lucre com rifas e sorteios no Brasil | Legal Prime',
  description:
    'Sua ideia vira campanha. Engajamento, vendas, arrecadação. Diagnóstico rápido, regulamento e SCPC quando precisar. Comece agora.',
  canonical: 'https://sorteios.com.br/',
} as const

export interface LandingFaqItem {
  readonly question: string
  readonly answer: string
}

export const indexLandingFaq: readonly LandingFaqItem[] = [
  {
    question: 'Rifa é permitida no Brasil',
    answer:
      'Rifa com bilhete numerado é proibida em regra. Lucro seguro costuma vir de promoção comercial em CNPJ ou operação filantrópica autorizada.',
  },
  {
    question: 'Como legalizar uma rifa',
    answer:
      'Primeiro enquadramos. Muita “rifa” vira promoção comercial ou ação beneficente autorizável. Você lucra no formato que a lei aceita.',
  },
  {
    question: 'Sorteio no Instagram precisa de autorização',
    answer:
      'Se vende, marca patrocina ou tem cadastro comercial, pode precisar de autorização federal. Publicar antes é risco. Valide antes de viralizar.',
  },
  {
    question: 'Posso sortear dinheiro ou Pix',
    answer:
      'Em promoção comercial, dinheiro e Pix costumam ser vetados. Prêmio em produto ou serviço mantém campanha forte e mais segura.',
  },
  {
    question: 'Pessoa física pode fazer sorteio com prêmio',
    answer:
      'Promoção comercial exige CNPJ. Creator sem empresa formal assume risco alto. Estruture antes de cobrar.',
  },
  {
    question: 'O que é SCPC',
    answer:
      'Sistema oficial do Ministério da Fazenda para autorizar promoção comercial com prêmios e prestar contas. É onde sua campanha ganha “CNH”.',
  },
  {
    question: 'Quanto tempo demora para autorizar sorteio',
    answer:
      'Tem prazo legal antes do start. Quanto antes montar, mais rápido você coloca dinheiro no caixa sem susto.',
  },
  {
    question: 'Concurso cultural precisa de autorização',
    answer:
      'Só é isento com mérito real e zero sorte disfarçado. Erro aqui derruba campanha e queima budget.',
  },
  {
    question: 'Quais são os riscos de um sorteio irregular',
    answer:
      'Multa, bloqueio, reputação no chão, conta congelada. Lucro de verdade exige compliance.',
  },
  {
    question: 'O que a consultoria faz',
    answer:
      'Transforma ideia em mecânica lucrativa e defendável: regulamento, protocolo, execução e contas fechadas.',
  },
] as const

export interface LandingFormatComparisonRow {
  readonly formato: string
  readonly resumo: string
  readonly perfilTipico: string
}

export const indexLandingFormatComparison: readonly LandingFormatComparisonRow[] = [
  {
    formato: 'Rifa bilhete numerado',
    resumo: 'Proibida na prática comum. Lucro limpo passa por outro modelo.',
    perfilTipico: 'Quem grita “rifa legalizada” nas redes.',
  },
  {
    formato: 'Promoção comercial',
    resumo: 'CNPJ, SCPC, prêmio bombando. É onde marca escala venda.',
    perfilTipico: 'Loja, infoprodutor, e-commerce.',
  },
  {
    formato: 'Filantropia autorizada',
    resumo: 'Bilhete com causa. Arrecada, impacta, cresce.',
    perfilTipico: 'ONG, igreja, projeto social.',
  },
  {
    formato: 'Concurso cultural',
    resumo: 'Só isento se for cultural de verdade. Sorte escondido é armadilha.',
    perfilTipico: 'Marca que quer engajar sem sorte.',
  },
] as const

export interface LandingDiagnosisStep {
  readonly step: string
  readonly title: string
  readonly line: string
}

export const indexLandingDiagnosisSteps: readonly LandingDiagnosisStep[] = [
  { step: '1', title: 'Sua ideia', line: 'Você manda o básico. A gente entende em minutos.' },
  { step: '2', title: 'Formato certo', line: 'Rifa, sorteio, promo. Nome importa menos que o lucro legal.' },
  { step: '3', title: 'Riscos zero drama', line: 'Pix, prêmio, PF. Listamos o que derruba campanha.' },
  { step: '4', title: 'Veredito', line: 'Vai, ajusta ou pivot. Sem enrolação.' },
  { step: '5', title: 'Go live', line: 'Documentos e próximos passos na mão. É só executar.' },
] as const

export const indexLandingDiagnosisBenefits: readonly { title: string; body: string }[] = [
  { title: 'Lucro sem susto', body: 'Sabe se o modelo paga antes de gastar com ads.' },
  { title: 'Autoridade instantânea', body: 'Campanha com cara de grande, mesmo começando pequeno.' },
  { title: 'Tempo é dinheiro', body: 'Menos ida e volta. Mais ROI.' },
  { title: 'Canal certo', body: 'Instagram, loja, evento. Ajustamos a narrativa ao formato que converte.' },
] as const

export const indexLandingDiagnosisPersonas: readonly { perfil: string; hook: string }[] = [
  { perfil: 'Creator', hook: 'Seguidores viram faturamento com prêmio certo.' },
  { perfil: 'Loja e marca', hook: 'Carrinho sobe quando o prêmio assusta.' },
  { perfil: 'ONG', hook: 'Causa que vende bilhete com propósito.' },
  { perfil: 'Agência', hook: 'Cliente feliz, campanha no ar, zero multa.' },
  { perfil: 'Plataforma de rifa', hook: 'Ferramenta não assina embaixo da lei. A gente assina o caminho.' },
] as const

/** Gera JSON-LD com Organization + FAQPage para uso em IndexPage (mesmas respostas do FAQ visível). */
export function buildIndexLandingJsonLd(organization: Record<string, unknown>): Record<string, unknown> {
  const faqEntity = indexLandingFaq.map((item) => ({
    '@type': 'Question',
    name: item.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: item.answer,
    },
  }))

  return {
    '@context': 'https://schema.org',
    '@graph': [
      organization,
      {
        '@type': 'FAQPage',
        mainEntity: faqEntity,
      },
    ],
  }
}
